import { baseApiService } from "./baseApiService";
import type { PagedResult } from "./types";
import { buildBlobProxyUrl } from "../utils/blobProxyUrl";

// User Document interfaces
export interface FileUploadRequestDto {
  base64File: string;
  fileName: string;
  contentType: string;
}

export interface CreateUserDocumentDto {
  userId: string;
  tittle: string; // Note: keeping the typo as in your backend DTO
  description?: string;
  question?: string;
  files: FileUploadRequestDto[];
  typeOfDocument: string;
  workflowType?: string;
  companyId?: string;
  companyName?: string;
}

export interface UserDocumentResponse {
  id: string;
  userId: string;
  tittle: string;
  description?: string;
  question?: string;
  typeOfDocument: string;
  documentLink: string;
  createdAt: string;
  updatedAt: string;
  documentSize?: string;
  createdDate?: string;
  companyId?: string;
  companyName?: string;
  jobId?: string;
  status?: string;
}

// API Response structure for document operations
export interface ApiResponse<T = unknown> {
  success: boolean;
  data: T;
  message: string;
}

// Q&A Response data structure
export interface QAResponseData {
  answer_found: boolean;
  answer_summary: string;
  supporting_quotes: string[];
  source_pages: number[];
}

export interface FileUploadUrlDto {
  fileUrl: string;
  fileName: string;
  fileSize?: string;
  mimeType?: string;
}

export interface CreateDocumentContradictionDto {
  userId: string;
  companyId?: string;
  title?: string;
  workflowType?: string;
  description?: string;
  referenceFile?: FileUploadRequestDto;
  referenceFileUrl?: string;
  targetFile?: FileUploadRequestDto;
  targetFileUrl?: string;
}

export interface WorkflowResponseDto {
  success: boolean;
  data?: unknown;
  message?: string;
  job_id?: string;
  status?: string;
}

export interface JobStatusResponseDto {
  job_id: string;
  status: string;
  message?: string;
  data?: unknown;
}

export interface WorkflowFileDto {
  fileName: string;
  fileUrl: string;
  fileType?: string;
}

export interface WorkflowHistoryDetailDto {
  _id: string;
  title: string;
  userId: string;
  companyId: string;
  workflow: string;
  workflowType: string;
  instruction?: string;
  timestamp: string;
  jobId?: string;
  status: string;
  files: WorkflowFileDto[];
  response?: any;
}

export interface UpdateUserDocumentDto {
  userId: string;
  tittle: string; // Note: keeping the typo as in your backend DTO
  description?: string;
  question?: string;
  workflowType?: string;
  files: FileUploadUrlDto[];
  typeOfDocument: string;
  companyId?: string;
  companyName?: string;
  selectedRules?: string[];
}

// User Document specific API calls
const normalizeDocument = (
  document: UserDocumentResponse,
): UserDocumentResponse => ({
  ...document,
  documentLink: buildBlobProxyUrl(document.documentLink),
});

export const userDocumentService = {
  // Get all documents
  getAll: () =>
    baseApiService
      .get<UserDocumentResponse[]>("/UserDocument")
      .then((documents) => documents.map(normalizeDocument)),

  // Get document by ID
  getById: (id: string) =>
    baseApiService
      .get<UserDocumentResponse>(`/UserDocument/${id}`)
      .then(normalizeDocument),

  // Create new document with file upload
  create: (dto: CreateUserDocumentDto) =>
    baseApiService.postWithTimeout<ApiResponse<QAResponseData>>(
      "/UserDocument",
      dto,
      600000
    ), // 10 minutes timeout

  // Update document
  update: (dto: UpdateUserDocumentDto) =>
    baseApiService.putWithTimeout<ApiResponse<UserDocumentResponse>>(
      "/UserDocument",
      dto,
      600000
    ), // 10 minutes timeout

  // Delete document
  delete: (id: string) => baseApiService.delete<void>(`/UserDocument/${id}`),

  // Get documents by user ID (POST request) with pagination support
  getByUserId: (
    pageNumber?: number,
    pageSize?: number,
    searchTerm?: string,
    companyId?: string
  ) => {
    // Build query parameters
    const queryParams = new URLSearchParams();
    if (pageNumber !== undefined) {
      queryParams.append("pageNumber", pageNumber.toString());
    }
    if (pageSize !== undefined) {
      queryParams.append("pageSize", pageSize.toString());
    }
    if (searchTerm) {
      queryParams.append("searchTerm", searchTerm);
    }
    if (companyId) {
      queryParams.append("companyId", companyId);
    }

    const queryString = queryParams.toString();
    const url = `/UserDocument/DocumentByUserId${
      queryString ? `?${queryString}` : ""
    }`;

    // Always return PagedResult
    return baseApiService.get<PagedResult<UserDocumentResponse>>(url).then(
      (result) => ({
        ...result,
        items: result.items.map(normalizeDocument),
      }),
    );
  },

  // Upload file for existing document
  uploadFile: (
    documentId: string,
    file: File,
    additionalData?: Record<string, string>
  ) =>
    baseApiService.uploadFile<string[]>(
      `/UserDocument/${documentId}/upload`,
      file,
      additionalData
    ),

  // Create document contradiction analysis
  createDocumentContradiction: (dto: CreateDocumentContradictionDto) =>
    baseApiService.postWithTimeout<WorkflowResponseDto>(
      "/UserDocument/document-contradiction",
      dto,
      600000
    ), // 10 minutes timeout

  // Get job status for polling
  getJobStatus: (jobId: string) =>
    baseApiService.get<WorkflowHistoryDetailDto>(`/Workflow/status/${jobId}`),
};
